import { AssetData, SEOConfig } from '@betterfunnel/types';
import { useEffect, useState } from 'react';
import AlertModal from '../Common/AlertModal';
import Button from '../Common/Button';
import FileUploadInput from '../FormFields/FileUpload/FileUploadInput';
import SettingSectionChipsField from './SettingSectionChipsField';
import SettingSectionTextArea from './SettingSectionTextArea';

interface BaseProps {
  seo: SEOConfig;
  onChange: (change: Partial<SEOConfig>, seo: SEOConfig) => void;
}

interface NormalProps extends BaseProps {
  showReset?: false;
}

interface ShowResetProps extends BaseProps {
  showReset:
    | true
    | {
        account?: boolean;
        dynamicPage?: boolean;
      };
  onReset: (type: 'account' | 'dynamicPage') => void;
}

type Props = NormalProps | ShowResetProps;

export const SEOForm = ({ seo, onChange, ...props }: Props) => {
  const [seoTitle, setSeoTitle] = useState<string>(seo.title || '');
  const [seoDescription, setSeoDescription] = useState<string>(
    seo.description || '',
  );
  const [seoKeywords, setSeoKeywords] = useState<string[]>(seo.keywords || []);
  const [seoImage, setSeoImage] = useState<AssetData | undefined>(seo.image);
  const [favicon, setFavicon] = useState<AssetData | undefined>(seo.favicon);

  const [showResetModal, setShowResetModal] = useState<{
    type: 'account' | 'dynamicPage';
  }>();

  useEffect(() => {
    setSeoTitle(seo.title || '');
    setSeoDescription(seo.description || '');
    setSeoKeywords(seo.keywords || []);
    setSeoImage(seo.image);
    setFavicon(seo.favicon);
  }, [seo]);

  return (
    <div className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
      <AlertModal
        title={`Reset to ${showResetModal?.type === 'account' ? 'Account' : 'Dynamic Page'} Defaults`}
        open={!!showResetModal}
        onClose={() => setShowResetModal(undefined)}
        actions={[
          {
            type: 'secondary',
            title: 'Cancel',
            onClick: () => setShowResetModal(undefined),
          },
          {
            type: 'primary',
            color: 'negative',
            title: 'Reset',
            onClick: () => {
              if ('onReset' in props) {
                const reset =
                  showResetModal?.type === 'account'
                    ? 'account'
                    : 'dynamicPage';
                props?.onReset?.(reset);
                setShowResetModal(undefined);
              }
            },
          },
        ]}
        type={'warn'}
      />
      <SettingSectionTextArea
        title="Title"
        value={seoTitle}
        setValue={setSeoTitle}
        rows={1}
        update={() => {
          onChange(
            { title: seoTitle },
            {
              ...seo,
              title: seoTitle,
            },
          );
        }}
        editable={true}
      />
      <SettingSectionTextArea
        title="Description"
        value={seoDescription}
        setValue={setSeoDescription}
        update={() => {
          onChange(
            { description: seoDescription },
            {
              ...seo,
              description: seoDescription,
            },
          );
        }}
        editable={true}
      />
      <SettingSectionChipsField
        title="Keywords"
        selected={seoKeywords?.map((k) => ({ id: k, title: k })) || []}
        setSelected={(selected) => setSeoKeywords(selected.map((s) => s.title))}
        update={() => {
          onChange(
            { keywords: seoKeywords },
            {
              ...seo,
              keywords: seoKeywords,
            },
          );
        }}
        editable={true}
        options={[]}
      />
      <div className="pt-6 sm:flex sm:flex-wrap sm:gap-2">
        <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
          Image
        </dt>
        <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
          <div className="w-full min-w-28 max-w-[415px]">
            <FileUploadInput
              image={seoImage}
              onSelected={(image) => {
                setSeoImage(image);
                onChange(
                  { image: image || (null as any) },
                  {
                    ...seo,
                    image: image || (null as any),
                  },
                );
              }}
            />
          </div>
        </dd>
      </div>
      <div className="pt-6 sm:flex sm:flex-wrap sm:gap-2">
        <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
          Favicon
        </dt>
        <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
          <div className="w-full min-w-28 max-w-[415px]">
            <FileUploadInput
              image={favicon}
              onSelected={(image) => {
                setFavicon(image);
                onChange(
                  { favicon: image || (null as any) },
                  {
                    ...seo,
                    favicon: image || (null as any),
                  },
                );
              }}
            />
          </div>
        </dd>
      </div>

      {'showReset' in props &&
        (props.showReset === true ||
          (typeof props.showReset === 'object' &&
            !!Object.keys(props.showReset).length)) && (
          <div className="pb-10 pt-16">
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Reset
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-500">
              Reset config to default.
            </p>

            <dl className="mb-3 mt-6 flex flex-col items-start justify-start gap-2 border-t border-gray-200 pt-4 text-sm leading-6">
              {(props.showReset === true || props.showReset?.account) && (
                <Button
                  variant="ternary"
                  color="primary"
                  onClick={() => setShowResetModal({ type: 'account' })}
                >
                  Reset to Account Defaults
                </Button>
              )}

              {(props.showReset === true || props.showReset?.dynamicPage) && (
                <Button
                  variant="ternary"
                  color="primary"
                  onClick={() => setShowResetModal({ type: 'dynamicPage' })}
                >
                  Reset to Dynamic Page Defaults
                </Button>
              )}
            </dl>
          </div>
        )}
    </div>
  );
};
